import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * Hook to manage URL query parameters for state synchronization
 * Useful for maintaining tabs, modals, and other UI state in the URL
 */
export function useURLState() {
  const history = useHistory();
  const location = useLocation();

  const searchParams = useMemo(() => {
    return new URLSearchParams(location.search);
  }, [location.search]);

  const getParam = useCallback(
    (key: string): string | null => {
      return searchParams.get(key);
    },
    [searchParams],
  );

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const newSearchParams = new URLSearchParams(location.search);

      if (value === null || value === '') {
        newSearchParams.delete(key);
      } else {
        newSearchParams.set(key, value);
      }

      const newSearch = newSearchParams.toString();
      const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

      history.replace(newPath);
    },
    [history, location.pathname, location.search],
  );

  const setParams = useCallback(
    (params: Record<string, string | null>) => {
      const newSearchParams = new URLSearchParams(location.search);

      Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === '') {
          newSearchParams.delete(key);
        } else {
          newSearchParams.set(key, value);
        }
      });

      const newSearch = newSearchParams.toString();
      const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

      history.replace(newPath);
    },
    [history, location.pathname, location.search],
  );

  const getAllParams = useCallback((): Record<string, string> => {
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }, [searchParams]);

  return {
    getParam,
    setParam,
    setParams,
    getAllParams,
    searchParams,
  };
}
